import React, {useContext, useEffect, useState} from "react";

// Services
import {NotificationsContext} from "../../../ui-components/Notifications";
import apiRequest from "../../../services/apiRequest";
import CountryContext from "../../../CountryContext";
import {useIntl} from "react-intl";
import AuthenticationService from "../../../services/AuthenticationService";
import CountryConfigContext from "../../../CountryConfigContext";


// Components
import CheckboxGroup from "../../../ui-components/CheckboxGroup";
import Placeholder from "../../../ui-components/Placeholder";
import {PageSection} from "../../../ui-components/Container";
import Select from "../../../ui-components/Select";
import Toggle from "../../../ui-components/Toggle";
import GameBarChart from "./GameBarChart";
import {WorksitesTable} from "./WorksitesTable";
import {GameSurveysDonutChart} from "./GameSurveysDonutChart";
import Checkbox from "../../../ui-components/Checkbox";
import SearchSelect from "../../../ui-components/SearchSelect";
import UsersHeatMap from "../Users/UsersHeatMap";

// Types
import {
  AnalyticsGameProps,
  Worksite,
  Survey,
  FilterWorksites,
  User,
  GeoWorksitesData,
  GeoUsersData,
  UserAmbito,
  SurveysPiechartData, AMChartDountData
} from "./Game.type";
// Lib
import {getRolesOptions, allUsers, initUsers} from "./Game.lib";
import {toArray} from "./DonutDataPreparer";
import {getVisibilityLevel} from "../Analytics.lib";
import {PRIVILEGED_ROLES} from "../../../constants";
import {InternalUser} from "../Analytics.type";
import {arraysOverlap} from "../Analytics.lib"
import {WorksiteTerminatedFilter} from "../WorksiteTerminatedFilter";



export function AnalyticsGame(
  {
    visibilityUserIds,
    analysisPeriod,
    worksiteTypes,
    internalUsers,
  }: AnalyticsGameProps
) {

  const intl = useIntl();
  const {country} = useContext(CountryContext);
  const { config } = useContext(CountryConfigContext);
  const {push} = useContext(NotificationsContext);
  const userData = AuthenticationService.getUserData();
  const rolesOptions = getRolesOptions(getVisibilityLevel(userData.role_type), intl)
  const userScopes = AuthenticationService.getUserScopes();

  const [selectedRole, setSelectedRole] = useState(rolesOptions.find(i=>i.value==='field') || rolesOptions[0]);
  const [ambitiOptions, setAmbitiOptions] = useState<{value: UserAmbito; label: string}[]>([]);
  const [selectedAmbiti, setSelectedAmbiti] = useState<{value: UserAmbito, label:string}[] | undefined>([]);
  const [selectedAreaManagers, setSelectedAreaManagers] = useState<{value: number, label:string}[] | undefined>([]);
  const [players, setPlayers] = useState<{label: string, value: number}[]>([]);
  const [worksiteMeasure, setWorksiteMeasure] = useState<'val' | 'amt'>('val');
  const [worksites, setWorksites] = useState<Worksite[] | undefined>(undefined);
  const [surveys, setSurveys] = useState<Survey[] | undefined>(undefined);
  const [surveysPiechartData, setSurveysPiechartData] = useState<SurveysPiechartData | undefined>(undefined);
  const [chartWorksites, setChartWorksites] = useState<any | undefined>(undefined);
  const [chartSurveys, setChartSurveys] = useState<any | undefined>(undefined);
  const [donutChartSurveys, setDonutChartSurveys] = useState<AMChartDountData[] | undefined>(undefined);

  const [filterWorksites, setFilterWorksites] = useState<FilterWorksites>({
    ownership: ['created'], status: ['planning', 'design', 'open', 'closed']
  });

  const [users, setUsers] = useState<User[] | undefined>(undefined);
  const [filterUsers, setFilterUsers] = useState<string[]>(initUsers);
  const [chartUsers, setChartUsers] = useState<any | undefined>(undefined);

  const [geoWorksites, setGeoWorksites] = useState<GeoWorksitesData[] | undefined>(undefined)
  const [geoUsers, setGeoUsers] = useState<GeoUsersData[] | undefined>(undefined)
  const [loadingMapUsers, setLoadingMapUsers] = useState(false);
  const [loadingWorksites, setLoadingWorksites] = useState(false);
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingMapWorksites, setLoadingMapWorksites] = useState(false);
  const [filterServices, setFilterServices] = useState<any[]>([])
  const [filterWorksiteType, setFilterWorksiteType] = useState([]);
  const [filterSurveysSpecifiedStatus, setFilterSurveysSpecifiedStatus] = useState<string[]>(['specified', 'won', 'lost']);
  const [specified, setSpecified] = useState(false);
  const services = (config.services as { active: boolean; name: string }[])
    .filter((i) => i.active)
    .map((i) => i.name)
    .sort();

  const [worksiteTerminationFilter, setWorksiteTerminationFilter] = useState<string>()
  const [worksiteTerminationReasonFilter, setWorksiteTerminationReasonFilter] = useState<string>()

  useEffect(() => {
    const fetchAmbiti = async () => {
      try {
        const res = await apiRequest.get("users/fields-ambiti")
        const options = res.map((i:any) => ({
          value: i.ambito as UserAmbito,
          label: `${intl.formatMessage({id:i.ambito as string})} (${i.field_manager})`
        }))
        setAmbitiOptions(options)
      } catch (error) {
        console.log(error)
      }
    }
    fetchAmbiti()
  }, [])

  useEffect(() => {
    console.log("fetching worksites")
    const payload = {
      ownership: filterWorksites.ownership,
      status: filterWorksites.status,
      country: country.code,
      start_date: analysisPeriod.start,
      end_date: analysisPeriod.end,
      visibility_user_ids: visibilityUserIds,
      role: selectedRole.value,
      ...(selectedAmbiti ? {ambiti: selectedAmbiti.map(i=>i.value)} : {}),
      ...(filterServices.length ? {services:filterServices.map(i => i.value)} : {}),
      ...(filterWorksiteType.length ? {worksite_types:filterWorksiteType.map((i: any) => i.value)} : {}),
      ...(specified ? {specified: true, specified_statuses: filterSurveysSpecifiedStatus} : {}),
      ...(selectedAreaManagers ? {area_manager_ids: selectedAreaManagers.map(i => i.value)} : {}),
      ...(players && players.length ? {players: players.map(i => i.value)} : {}),
      ...(worksiteTerminationFilter ? { worksite_termination: worksiteTerminationFilter } : {}),
      ...(worksiteTerminationReasonFilter ? { worksite_termination_reason: worksiteTerminationReasonFilter } : {}),
    }
    const fetchWorksites = async () => {
      setLoadingWorksites(true)
      setGeoWorksites(undefined)
      setWorksites(undefined)
      try {
        const worksites = await apiRequest.post("/analytics/game_worksites", payload);
        // @ts-ignore
        setWorksites(worksites)
        setLoadingWorksites(false)
      } catch (error) {
        console.log(error)
        setWorksites([])
        push({title: intl.formatMessage({id: "server_error"}), type: "error"});
        setLoadingWorksites(false)
      }
    };
    fetchWorksites()
  }, [
    visibilityUserIds,
    analysisPeriod,
    selectedRole,
    specified,
    filterSurveysSpecifiedStatus,
    filterWorksiteType,
    filterServices,
    selectedAmbiti,
    filterWorksites,
    selectedAreaManagers,
    players,
    worksiteTerminationFilter,
    worksiteTerminationReasonFilter
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const payload = {
          country: country.code,
          start_date: analysisPeriod.start,
          end_date: analysisPeriod.end,
          visibility_user_ids: visibilityUserIds,
          role: selectedRole.value,
        }
        const users = await apiRequest.post("/analytics/game_users", payload);
        // @ts-ignore
        setUsers(users)
        setLoadingUsers(false);
      } catch (error) {
        console.log(error)
        setUsers([])
        push({title: intl.formatMessage({id: "server_error"}), type: "error"});
        setLoadingUsers(false);
      }
    }
    fetchUsers()
  }, [
    visibilityUserIds,
    analysisPeriod,
    selectedRole,
    specified,
    filterWorksiteType,
    filterServices,
  ]);

  useEffect(() => {

    const payload = {
      country: country.code,
      start_date: analysisPeriod.start,
      end_date: analysisPeriod.end,
      visibility_user_ids: visibilityUserIds,
      role: selectedRole.value,
      ownership: filterWorksites.ownership,
      status: filterWorksites.status,
      ...(filterWorksiteType.length
        ? {worksite_types: filterWorksiteType.map((i: any) => i.value)}
        : {}),
      ...(filterServices.length
        ? {services: filterServices.map(i => i.value)}
        : {}),
      ...(specified ? {specified: true, specified_statuses: filterSurveysSpecifiedStatus} : {}),
      ...(selectedAmbiti
        ? {ambiti: selectedAmbiti.map(s => s.value as UserAmbito)}
        : {}),
      ...(selectedAreaManagers
        ? {area_manager_ids: selectedAreaManagers.map((i: any) => i.value)}
        : {}),
      ...(players && players.length ? {players: players.map(i => i.value)} : {}),
      ...(worksiteTerminationFilter ? { worksite_termination: worksiteTerminationFilter } : {}),
      ...(worksiteTerminationReasonFilter ? { worksite_termination_reason: worksiteTerminationReasonFilter } : {}),
    }
    const fetchGeoWorksites = async () => {
      setGeoWorksites(undefined);
      setLoadingWorksites(true);

      try {
        const res = await apiRequest.post("/analytics/game/geo/worksites", payload);

        // @ts-ignore
        setGeoWorksites(res);
      } catch (error) {
        push({
          title: intl.formatMessage({id: "server_error"}),
          type: "error",
        });
        setGeoWorksites([]);
      } finally {
        setLoadingWorksites(false);
      }
    };

    const fetchGameSurveys = async () => {
      setLoadingSurveys(true);
      try {
        const [surveysBarchart, surveysPieChart] = await Promise.all([
            apiRequest.post("/analytics/game_surveys", payload),
            apiRequest.post("/analytics/game_surveys_piechart", payload)
          ]
        );
        setSurveys(surveysBarchart);
        setSurveysPiechartData(surveysPieChart);
        setLoadingSurveys(false);
      } catch (error) {
        console.log(error)
        setSurveys([]);
        push({title: intl.formatMessage({id: "server_error"}), type: "error"});
        setLoadingSurveys(false);
      } finally {
        setLoadingSurveys(false);
      }
    }
    fetchGeoWorksites();
    if (specified)
      fetchGameSurveys();
  }, [
    visibilityUserIds,
    analysisPeriod,
    selectedRole,
    filterWorksites,
    filterWorksiteType,
    filterServices,
    specified,
    selectedAmbiti,
    filterSurveysSpecifiedStatus,
    selectedAreaManagers,
    players,
    worksiteTerminationFilter,
    worksiteTerminationReasonFilter,
  ]);

  useEffect(() => {
    setGeoUsers(undefined)
    setLoadingMapUsers(true)
    apiRequest.post("/analytics/game/geo/users", {
      country: country.code,
      start_date: analysisPeriod.start,
      end_date: analysisPeriod.end,
      visibility_user_ids: visibilityUserIds,
      role: selectedRole.value,
      status: filterUsers,
    })
      .then((res:any) => {
        //@ts-ignore
        setGeoUsers(res)
      })
      .catch(() => {
        push({title: intl.formatMessage({id: "server_error"}), type: "error"})
        setGeoUsers([])
      })
      .finally(() => setLoadingMapUsers(false))

  }, [visibilityUserIds, analysisPeriod, selectedRole, filterUsers]);


  useEffect(() => {
    if (worksites) {
      const final = worksites.map(i => {
        let row: { [k: string]: any } = {fullname: i.name, total: 0}
        filterWorksites.status.forEach(s => {
          // @ts-ignore
          const val = i[s][worksiteMeasure] || 0
          row[s] = val
          row.total += val
        })
        return row
      })
        .filter(i => i.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 25)
        .map((row, index) => ({...row, fullname: `${index + 1}° ${row.fullname}`}))
      setChartWorksites(final)
    }
  }, [worksites, filterWorksites, worksiteMeasure])

  useEffect(() => {
    if (surveys) {
      const final = surveys.map(i => {
        let row: { [k: string]: any } = {fullname: i.name, total: 0}
        for (const status of filterSurveysSpecifiedStatus) {
          // @ts-ignore
          const val = i['survey_' + status]?.[worksiteMeasure] || 0
          row['analytics_survey_' + status] = val
          row.total += val
        }
        return row
      })
        .filter((i: any) => i.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 25)
        .map((row, index: number) => ({...row, fullname: `${index + 1}° ${row.fullname}`}))
      setChartSurveys(final);
    }
  }, [surveys, worksiteMeasure, filterSurveysSpecifiedStatus, specified]);

  useEffect(() => {
    if (surveysPiechartData) {
      const prepared = toArray(surveysPiechartData, intl,{
        // now pick the order based on filterSurveysSpecifiedStatus
        order: filterSurveysSpecifiedStatus.map(status => status) as ('won' | 'lost' | 'specified')[], // secure cast
        measure: worksiteMeasure
      })
      setDonutChartSurveys(prepared);
    }
  }, [filterSurveysSpecifiedStatus, surveysPiechartData, worksiteMeasure])

  useEffect(() => {
    if (users) {
      const res = users.map(i => {
        let row: { [k: string]: any } = {fullname: i.name, total: 0}
        filterUsers.forEach(userType => {
          // @ts-ignore
          const val = i[userType]
          row[userType] = val
          row.total += val
        })
        return row
      })
      const final = res.filter(i => i.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 25)
        .map((row, index) => ({...row, fullname: `${index + 1}° ${row.fullname}`}))
      setChartUsers(final)
    }
  }, [users, filterUsers])

  // whenever the selected role changes reset the selected ambiti, area managers and players
  useEffect(() => {
    if (selectedAmbiti && selectedAmbiti.length > 0)
      setSelectedAmbiti([]);
    if (selectedAreaManagers && selectedAreaManagers.length > 0)
      setSelectedAreaManagers([]);
    if (players && players.length > 0)
      setPlayers([]);
  }, [selectedRole])

  // whenever the selected ambiti changes check if the selected players have the selected ambiti in their profile, if not reset the selected players
  useEffect(() => {
    if (selectedAmbiti && selectedAmbiti.length > 0 && players && players.length > 0) {
      const filteredPlayers = players.filter(p => {
        const user = internalUsers.find((u: any) => u.id === p.value);
        if (!user) return false;
        if (!user.ambito) return false;
        return arraysOverlap(user.ambito, selectedAmbiti.map(i => i.value as UserAmbito));
      })
      if (filteredPlayers.length !== players.length) {
        setPlayers(filteredPlayers);
      }
    }
  }, [selectedAmbiti])

  // whenever the area managers changes check if the selected players have the selected area managers in their hierarchy, if not reset the selected players
  useEffect(() => {
    if (selectedAreaManagers && selectedAreaManagers.length > 0 && players && players.length > 0) {
      const filteredPlayers = players.filter(p => {
        const user = internalUsers.find((u: any) => u.id === p.value);
        if (!user) return false;
        if (!user.role_hierarchy) return false;
        return arraysOverlap(user.role_hierarchy.split(';'), selectedAreaManagers.map(i => `user_${i.value}`));
      })
      if (filteredPlayers.length !== players.length) {
        setPlayers(filteredPlayers);
      }
    }
  }, [selectedAreaManagers])


  return <div className="w-full">
    {rolesOptions.length > 1 ?
      <div className="w-full flex flex-col items-center">
        <h1 className="text-gray-600">Player Type</h1>
        <SearchSelect
          label={undefined}
          value={selectedRole}
          options={rolesOptions}
          onChange={(e: any) => {
            setSelectedAmbiti(undefined);
            setSelectedRole(e);
          }}
          loadOptions={undefined}
          isMulti={false}
          CustomOption={undefined}
          CustomGroup={undefined}
        /> {selectedRole.value === 'field' &&
        <div className={"w-full flex flex-col items-center"}>
          <h1 className={'text-gray-600'}>{intl.formatMessage({
            id: 'specialization',
            defaultMessage: 'specializzazione'
          })}</h1>
          <SearchSelect
            label={undefined}
            value={selectedAmbiti}
            onChange={(e: any) => setSelectedAmbiti(e)}
            options={ambitiOptions}
            isMulti={true}
            loadOptions={undefined}
            CustomOption={undefined}
            CustomGroup={undefined}
          />
        </div>
      } {
          selectedRole.value === 'agent' && PRIVILEGED_ROLES.concat(['field']).includes(userData.role_type) &&
          <div className={"w-full flex flex-col items-center"}>
            <h1 className={'text-gray-600'}>{intl.formatMessage({
              id: 'area_manager',
              defaultMessage: 'area manager'
            })}</h1>
            <SearchSelect
              label={undefined}
              value={selectedAreaManagers}
              onChange={(e: any) => setSelectedAreaManagers(e)}
              options={internalUsers
                .filter((i: any) => i.role.name === 'area_manager')
                .map((i: any) => ({value: i.id, label: `${i.lastname} ${i.firstname}`}))}
              isMulti={true}
              loadOptions={undefined}
              CustomOption={undefined}
              CustomGroup={undefined}
            />
          </div>
      }
      <div className={"w-full flex flex-col items-center"}>
        <h1 className={'text-gray-600'}>{intl.formatMessage({
          id: 'players',
          defaultMessage: 'Players'
        })}</h1>
        <SearchSelect
          label={undefined}
          value={players}
          onChange={(e: any) => setPlayers(e)}
          options={internalUsers.filter((u: InternalUser) => {
            /*
              filter users that have:
              1) same role as selectedRole
              2) if selectedRole is field, only users that have an ambito that is in selectedAmbiti (also include field_manager role)
              3) if selectedRole is agent and area_mangers are selected, only users that have the selected area managers in their hierarchy
            */
            if (selectedRole.value === 'field') {
              if (u.role.name !== 'field' && u.role.name !== 'field_manager') return false;
            } else if (u.role.name !== selectedRole.value) {
              return false;
            }
            if (selectedRole.value === 'field' && selectedAmbiti && selectedAmbiti.length > 0) {
              if (!u.ambito) return false;
              return arraysOverlap(u.ambito, selectedAmbiti.map(i => i.value as UserAmbito));
            }
            if (selectedRole.value === 'agent' && selectedAreaManagers && selectedAreaManagers.length > 0) {
              return arraysOverlap(u.role_hierarchy.split(';'), selectedAreaManagers.map(i => `user_${i.value}`));
            }
            return true;
          }).map((i: any) => ({value: i.id, label: `${i.lastname} ${i.firstname}`}))}
          loadOptions={undefined}
          CustomOption={undefined}
          CustomGroup={undefined}
          isMulti={true}
        />
      </div>
      </div> : null
    }
    <PageSection
      title={<span className="font-bold">{intl.formatMessage({id: "worksites"})}</span>}
      description={undefined}
    >
      {worksites && chartWorksites ?
        <div className="w-full flex flex-col items-center">
          <div className="w-full flex flex-col grid grid-cols-3 sm:flex-row justify-around items-center gap-x-5">
            {/*@ts-ignore*/}
            <CheckboxGroup
              title="Ownership"
              horizontal
              options={['created', 'shared', 'visible'].map(i => ({value: i, name: intl.formatMessage({id: i})}))}
              values={filterWorksites.ownership}
              onChange={(value: string) => {
                if (filterWorksites.ownership.indexOf(value) >= 0) {
                  setFilterWorksites({
                    ...filterWorksites,
                    ownership: [...filterWorksites.ownership.filter((v) => v !== value)]
                  });
                } else {
                  setFilterWorksites({...filterWorksites, ownership: [...filterWorksites.ownership, value]});
                }
              }}
            />
              <SearchSelect
                label={intl.formatMessage({
                  id: "worksite_type",
                  defaultMessage: "Tipo cantiere",
                })}
                value={filterWorksiteType}
                onChange={(e: any) => setFilterWorksiteType(e)}
                options={worksiteTypes?.map((i) => ({
                  value: i.cod_worksite_type,
                  label: i.des_worksite_type,
                }))}
                isMulti
                loadOptions={undefined}
                CustomOption={undefined}
                CustomGroup={undefined}
              />
              <SearchSelect
                label={intl.formatMessage({
                  id: "configurations",
                  defaultMessage: "Configurazioni",
                })}
                value={filterServices}
                options={config.services.map((el:any) => ({
                  value: el.id,
                  label: el.name,
                }))}
                onChange={(e: any) => setFilterServices(e)}
                isMulti
                loadOptions={undefined}
                CustomOption={undefined}
                CustomGroup={undefined}
              />
            <WorksiteTerminatedFilter
              worksiteTerminationFilter={worksiteTerminationFilter}
              setWorksiteTerminationFilter={setWorksiteTerminationFilter}
              worksiteTerminationReasonFilter={worksiteTerminationReasonFilter}
              setWorksiteTerminationReasonFilter={setWorksiteTerminationReasonFilter}
            />
            {userScopes.includes("service_survey:read_specified") && <div className={"flex flex-col gap-y-1 pb-2 items-center justify-start"}>
              <span>{intl.formatMessage({
                id: "specified",
                defaultMessage: "specificate"
              })}</span>
              <Checkbox
                name={intl.formatMessage({id: "specified", defaultMessage: "Specificato"})}
                checked={specified}
                onChange={() => {
                  setSpecified(!specified);
                }}
                id={undefined}
              />
              {specified &&
                <div className={"flex flex-col items-center justify-center"}>
                  <span>{intl.formatMessage({
                    id: "specified_statuses",
                    defaultMessage: "Stati specifica"
                  })}</span>
                  <CheckboxGroup
                    horizontal
                    options={['specified', 'won', 'lost'].map(i => ({
                      value: i,
                      name: intl.formatMessage({id: 'analytics_survey_' + i})
                    }))}
                    values={filterSurveysSpecifiedStatus}
                    onChange={(value: string) => {
                      if (filterSurveysSpecifiedStatus.indexOf(value) >= 0) {
                        setFilterSurveysSpecifiedStatus([...filterSurveysSpecifiedStatus.filter((v) => v !== value)]);
                      } else {
                        setFilterSurveysSpecifiedStatus([...filterSurveysSpecifiedStatus, value]);
                      }
                    }}
                    title={undefined}
                    description={undefined}
                  />
                </div>}
            </div>
            }
            <div className="flex flex-row gap-x-2 font-bolder text-lg justify-center">
              <span>#</span>
              <div className="-mt-5">
                {/*@ts-ignore*/}
                <Toggle
                  checked={worksiteMeasure === 'amt'}
                  onChange={() => {
                    setWorksiteMeasure(worksiteMeasure === 'val' ? 'amt' : 'val')
                  }}
                />
              </div>
              <span>€</span>
            </div>
            {/*@ts-ignore*/}
            <CheckboxGroup
              title={intl.formatMessage({id: "status", defaultMessage: "Stato"})}
              horizontal
              options={['planning', 'design', 'open', 'closed'].map(i => ({
                value: i,
                name: intl.formatMessage({id: i})
              }))}
              values={filterWorksites.status}
              onChange={(value: string) => {
                if (filterWorksites.status.indexOf(value) >= 0) {
                  setFilterWorksites({
                    ...filterWorksites,
                    status: [...filterWorksites.status.filter((v) => v !== value)]
                  });
                } else {
                  setFilterWorksites({...filterWorksites, status: [...filterWorksites.status, value]});
                }
              }}
            />
          </div>
          {!(loadingWorksites || loadingMapWorksites) ?
            <div className="w-full flex flex-col lg:flex-row my-4 items-center mt-5">
              <div className={`w-full ${country.code === "it" ? "lg:w-2/3" : ""}`}>
                <GameBarChart
                  id="GamesWorksitesBarChart"
                  data={chartWorksites}
                  statusList={filterWorksites.status}
                  showPrice={worksiteMeasure === 'amt'}
                  intl={intl}
                />
              </div>
              {
                country.code === "it"
                  ? (
                    <div className="w-full lg:w-1/3">
                      {geoWorksites ?
                        <UsersHeatMap
                          id="GeoWorksitesHeatMap"
                          data={geoWorksites.map(i => ({
                            value: worksiteMeasure === 'amt' ? i.amt_total : i.num_worksites,
                            id: 'IT-' + i.cod_province
                          }))}
                        /> :
                        null
                      }
                    </div>
                  )
                  : null
              }
            </div> : <Placeholder height="h-80"/>}
          {specified && chartSurveys ?
            !loadingSurveys ?
              <div className="w-full flex flex-col lg:flex-row my-4 items-center mt-5">
                <div className={`w-full ${country.code === "it" ? "lg:w-2/3" : ""}`}>
                  <GameBarChart
                    id="GamesSurveysBarChart"
                    data={chartSurveys}
                    statusList={['analytics_survey_specified', 'analytics_survey_unspecified', 'analytics_survey_won', 'analytics_survey_lost']}
                    showPrice={worksiteMeasure === 'amt'}
                    intl={intl}
                  />
                </div>
                <div className="w-full lg:w-1/3">
                  <GameSurveysDonutChart
                    data={donutChartSurveys}
                    intl={intl}
                    id={'GameSurveysPiechart'}
                    worksiteMeasure={worksiteMeasure}
                  />
                </div>
              </div> : <Placeholder height="h-80"/> : null}
        </div>
        : <div>
          <Placeholder height="h-80"/>
        </div>
      }
      {userScopes.includes("game_analytics_table:read") && selectedRole && <div>
        <WorksitesTable
          visibility_user_ids={visibilityUserIds}
          country={country.code}
          start_date={analysisPeriod.start}
          end_date={analysisPeriod.end}
          role={selectedRole.value}
          ambiti={selectedAmbiti ? selectedAmbiti.map(s=>s.value as UserAmbito) : undefined}
          ownership={filterWorksites.ownership}
          statuses={filterWorksites.status}
          {...(filterWorksiteType.length ? {worksite_types: filterWorksiteType.map((i: any) => i.value)} : {})}
          {...(filterServices.length ? {services: filterServices.map(i => i.value)} : {})}
          {...(specified ? {specified: true, specified_statuses: filterSurveysSpecifiedStatus} : {})}
          {...(selectedAreaManagers ? {selectedAreaManagers: selectedAreaManagers.map((i: any) => i.value)} : {})}
          {...(players && players.length > 0 ? {players: players.map(i => i.value)} : {})}
          worksiteTerminationFilter={worksiteTerminationFilter}
          worksiteTerminationReasonFilter={worksiteTerminationReasonFilter}
        />
      </div>}
    </PageSection>
    <PageSection
      title={<span className="font-bold">{intl.formatMessage({id: "users"})}</span>}
      description={undefined}
    >
      {users && chartUsers ?
        <div className="w-full flex flex-col items-center">
          <div className="w-full flex flex-col sm:flex-row justify-around items-center">
            {/*@ts-ignore*/}
            <CheckboxGroup
              title={intl.formatMessage({id: "status", defaultMessage: "Stato"})}
              horizontal
              options={allUsers.map(i => ({value: i, name: intl.formatMessage({id: i})}))}
              values={filterUsers}
              onChange={(value: string) => {
                if (filterUsers.indexOf(value) >= 0) {
                  setFilterUsers([...filterUsers.filter((v) => v !== value)]);
                } else {
                  setFilterUsers([...filterUsers, value]);
                }
              }}
            />
          </div>
          <div className="w-full flex flex-col lg:flex-row my-4 items-center mt-5">
            <div className={`w-full ${country.code === "it" ? "lg:w-2/3" : ""}`}>
              <GameBarChart
                id="GamesUsersBarChart"
                data={chartUsers}
                statusList={filterUsers}
                showPrice={false}
                intl={intl}
              />
            </div>
            {
              country.code === "it"
                ? (
                  <div className="w-full lg:w-1/3">
                    {loadingMapUsers ? <div className="p-4">
                      <Placeholder height="h-80"/>
                    </div> : geoUsers ?
                      <UsersHeatMap
                        id="GeoUsersHeatMap"
                        data={geoUsers.map(i => ({value: i.num_users, id: 'IT-' + i.cod_province}))}
                      /> :
                      null
                    }
                  </div>
                )
                : null
            }
          </div>
        </div>
        : <div>
          <Placeholder height="h-80"/>
        </div>
      }
    </PageSection>
  </div>
}